/// <amd-module name="CodeCoverage/CodeCoverageCiTab/Components/ResponsiveCodeCoverageCiSummaryView" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { Datum } from "VSS/Features/Charts/Contracts";
import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { IBreakPoint, ResponsiveViewport } from "VSSUI/ResponsiveViewport";
import { css, getSafeId } from "VSSUI/Util";

import { DonutChartColorMap } from "CodeCoverage/CodeCoverageCiTab/Components/Constants";
import { CoverageDonutChart } from "CodeCoverage/CodeCoverageCiTab/Components/CoverageDonutChart";
import * as Resources from "CodeCoverage/CodeCoverageCiTab/Resources";
import { CoverageSummary } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";

interface ICodeCoverageCiSummaryBreakPoint extends IBreakPoint {
    isCompact: boolean;
}

export interface IResponsiveCodeCoverageCiSummaryProps {
    coverageSummary: CoverageSummary;
    coverageChanged: number;
}

export class ResponsiveCodeCoverageCiSummaryView extends VssComponent<IResponsiveCodeCoverageCiSummaryProps> {
    public render(): JSX.Element {
        return (
            <ResponsiveViewport<ICodeCoverageCiSummaryBreakPoint>
                breakPoints={[
                    {
                        className: "ci-cc-summary-compact",
                        /** width of summary info + 4 columns at 124 (min-width: 108 + left-padding: 16).
                         * This should be kept in sync with variables in CodeCoverageCiSummaryView.scss
                         **/
                        maxWidth: 372 + 124 * 4,
                        isCompact: true
                    }
                ]}
                onRenderContent={this.onRenderCard}
            />
        );
    }

    private onRenderCard = (activeBreakpoints: ICodeCoverageCiSummaryBreakPoint[]): JSX.Element => {
        const isCompact = activeBreakpoints.some(breakpoint => breakpoint.isCompact);

        return (
            <CodeCoverageCiSummaryView
                coverageChanged={this.props.coverageChanged}
                coverageSummary={this.props.coverageSummary}
                isCompact={isCompact}
            />
        );
    };
}

export interface ICodeCoverageCiSummaryProps {
    coverageSummary: CoverageSummary;
    coverageChanged: number;
    isCompact?: boolean;
}

export class CodeCoverageCiSummaryView extends VssComponent<ICodeCoverageCiSummaryProps, {}> {
    public render(): JSX.Element {
        const coverageSummary = this.props.coverageSummary || {};
        const coveredLines = coverageSummary.covered || 0;
        const notCoveredLines = coverageSummary.notCovered || 0;
        const partiallyCoveredLines = coverageSummary.partiallyCovered || 0;
        const totalLines = coveredLines + notCoveredLines + partiallyCoveredLines;

        const isCompactCard = this.props.isCompact;

        return (
            <Card className="cc-ci-summary-card" titleProps={{ text: Resources.OverviewTitle, className: "margin-top-4" }}>
                <div className={isCompactCard ? "flex-column" : "flex-row"}>
                    <div className={"cc-chart-legend-ctnr flex-row"}>
                        {this.getDonutChart(coveredLines, notCoveredLines)}
                        {this.renderDonutChartLegend(coveredLines, totalLines)}
                    </div>
                    <div className={css("coverage-data body-m", isCompactCard ? "flex-column" : "flex-row")}>
                        {this.getComponent(totalLines, Resources.TotalCoveredLinesText, "total-lines", isCompactCard)}
                        {this.getComponent(coveredLines, Resources.CoveredLinesText, "covered-lines", isCompactCard)}
                        {this.getComponent(partiallyCoveredLines, Resources.PartialCoveredLinesText, "partially-covered-lines", isCompactCard)}
                        {this.getComponent(notCoveredLines, Resources.NotCoveredLinesText, "not-covered-lines", isCompactCard)}
                    </div>
                </div>
            </Card>
        );
    }

    private getDonutChart(coveredLines: number, notCoveredLines: number): JSX.Element {
        return <CoverageDonutChart chartData={this.getDonutChartData(coveredLines, notCoveredLines)} />;
    }

    private renderDonutChartLegend(coveredLines: number, totalLines: number): JSX.Element {
        const coveragePercentage: number = totalLines === 0 ? 0 : parseFloat(((coveredLines * 100) / totalLines).toFixed(2));
        const coverageChanged: number = this.props.coverageChanged || 0;

        let coverageChangeIcon: string;
        let coverageChangeIconCss: string = "body-m";

        if (coverageChanged > 0) {
            coverageChangeIcon = "StockUp";
        } else if (coverageChanged < 0) {
            coverageChangeIcon = "StockDown";
        } else {
            /* When coverage change is 0 */
            coverageChangeIcon = "DashKey";
        }

        return (
            <div className="flex-column margin-left-16">
                <div className="title body-m secondary-text">{Resources.CodeCoverageTitle}</div>
                <div className="flex-baseline primary-text flex-row">
                    <div className="coverage-changed-percentage">{format(Resources.CoveragePercentageFormat, coveragePercentage)}</div>
                    <div className="margin-left-16 flex-row">
                        <Icon className={css(coverageChangeIconCss)} iconName={coverageChangeIcon} />
                        <div className="margin-left-4 body-m">{format(Resources.CoveragePercentageFormat, Math.abs(coverageChanged).toFixed(2))}</div>
                    </div>
                </div>
            </div>
        );
    }

    private getDonutChartData(coveredLines: number, notCoveredLines: number): Datum[] {
        return [
            {
                name: Resources.CoveredLinesText,
                y: coveredLines,
                color: DonutChartColorMap.Covered
            },
            {
                name: Resources.NotCoveredLinesText,
                y: notCoveredLines,
                color: DonutChartColorMap.Missed
            }
        ];
    }

    private getComponent(count: number, text: string, labelId?: string, isCompact?: boolean): JSX.Element {
        return (
            <SummaryColumn label={text} isCompact={isCompact} labelId={labelId}>
                <div className="primary-text"> {format(Resources.Lines, count)}</div>
            </SummaryColumn>
        );
    }
}

interface ISummaryColumnProps extends IVssComponentProperties {
    label: string;
    labelId?: string;
    isCompact?: boolean;
}

const SummaryColumn: React.FunctionComponent<ISummaryColumnProps> = (props: ISummaryColumnProps) => {
    return (
        <div className={css("flex-grow", props.isCompact ? "flex-row flex-self-start" : "flex-column summary-column")}>
            <div className={css("secondary-text", props.isCompact && "summary-line-compact padding-vertical-4")} id={getSafeId(props.labelId)}>
                {props.label}
            </div>
            <div className={css("flex-center", props.isCompact ? "flex-column" : "flex-row margin-top-4")}>{props.children}</div>
        </div>
    );
};
